import React, { useEffect, useMemo, useState } from "react";

import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import CloseIcon from "@mui/icons-material/Close";
import { DataGrid } from "@mui/x-data-grid";
import { grey } from "@mui/material/colors";

import SubcategoryView from "../SubcategoryView/SubcategoryView";
import { buildCategoryColumns } from "../gridColumns";
import { bpGridSx } from "../storeBpTheme";
import { round1dp } from "../../data";
import "../../businessPlanV2.css";

const CategoryView = ({ submissionId, division, categories, loading, canEdit, onDataChanged }) => {
	const [selectedCategory, setSelectedCategory] = useState(null);

	useEffect(() => {
		setSelectedCategory(null);
	}, [division]);

	const { ltlTotalQty, newTotalQty, rows } = useMemo(() => {
		const ltlTotalQty = categories.reduce((s, r) => s + (r.ltl_qty || 0), 0);
		const newTotalQty = categories.reduce((s, r) => s + (r.new_qty || 0), 0);

		if (!categories.length) return { ltlTotalQty, newTotalQty, rows: [] };

		const ltlNsv = categories.reduce((s, r) => s + (r.ltl_nsv || 0), 0);
		const newNsv = categories.reduce((s, r) => s + (r.new_nsv || 0), 0);
		const ltlDispatch = categories.reduce((s, r) => s + (r.ltl_inventory_qty || 0), 0);
		const ltlCombineSale = categories.reduce(
			(s, r) => s + ((r.ltl_st || 0) / 100) * (r.ltl_inventory_qty || 0),
			0
		);

		const totalRow = {
			id: "TOTAL",
			isTotal: true,
			category: "TOTAL",
			ltl_qty: ltlTotalQty,
			new_qty: newTotalQty,
			ltl_dis: round1dp(
				categories.length ? categories.reduce((s, r) => s + (r.ltl_dis || 0), 0) / categories.length : 0
			),
			new_dis: round1dp(
				categories.length ? categories.reduce((s, r) => s + (r.new_dis || 0), 0) / categories.length : 0
			),
			ltl_opt: categories.reduce((s, r) => s + (r.ltl_opt || 0), 0),
			new_opt: categories.reduce((s, r) => s + (r.new_opt || 0), 0),
			ltl_st: ltlDispatch ? round1dp((ltlCombineSale / ltlDispatch) * 100) : 0,
			ltl_inventory_qty: ltlDispatch,
			ltl_asp: round1dp(ltlTotalQty ? ltlNsv / ltlTotalQty : 0),
			new_asp: round1dp(newTotalQty ? newNsv / newTotalQty : 0),
			ltl_nsv: round1dp(ltlNsv),
			new_nsv: round1dp(newNsv),
		};

		return { ltlTotalQty, newTotalQty, rows: [totalRow, ...categories] };
	}, [categories]);

	const columns = useMemo(() => buildCategoryColumns(ltlTotalQty, newTotalQty), [ltlTotalQty, newTotalQty]);

	const handleRowClick = ({ row }) => {
		if (row.isTotal) return;
		setSelectedCategory(row);
	};

	const getRowClassName = ({ row }) => {
		if (row.isTotal) return "";
		if (row.completed) return row.total_count === 0 ? "yellow" : "green";
		if (row.total_count === 0 && row.ltl_qty === 0) return "yellow";
		return "";
	};

	return (
		<Stack sx={{ width: "100%" }} spacing={1}>
			<Box display="flex" width="100%">
				<DataGrid
					rows={rows}
					columns={columns}
					columnHeaderHeight={35}
					rowHeight={36}
					showCellVerticalBorder
					showColumnVerticalBorder
					getRowClassName={getRowClassName}
					onRowClick={handleRowClick}
					hideFooter
					disableRowSelectionOnClick
					sx={bpGridSx}
				/>
			</Box>

			<Dialog open={loading}>
				<Box width="200px" height="100px" display="flex" alignItems="center" justifyContent="center">
					<CircularProgress />
				</Box>
			</Dialog>

			<Dialog
				open={Boolean(selectedCategory)}
				onClose={() => setSelectedCategory(null)}
				maxWidth="lg"
				fullWidth
				PaperProps={{ sx: { borderRadius: 2 } }}
			>
				<DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1 }}>
					<Typography variant="h6" fontWeight={700}>
						{selectedCategory?.category}
					</Typography>
					<IconButton onClick={() => setSelectedCategory(null)} size="small">
						<CloseIcon fontSize="small" />
					</IconButton>
				</DialogTitle>
				<DialogContent dividers sx={{ bgcolor: grey[50] }}>
					{selectedCategory && (
						<SubcategoryView
							submissionId={submissionId}
							category={selectedCategory.category}
							division={selectedCategory.division}
							canEdit={canEdit}
							onDataChanged={onDataChanged}
							embedded
						/>
					)}
				</DialogContent>
			</Dialog>
		</Stack>
	);
};

export default CategoryView;
